import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getVoicesConfig,
  getVoicesWorkspaces,
  removeVoicesBufferKey,
  setVoicesBufferKey,
  updateVoicesConfig,
  getVoicesSchedules,
  updateVoicesSchedule,
} from "../Api/voicesConfig";
import {
  VoicesCompanyVoice,
  VoicesConfig,
  VoicesConfigPayload,
  VoicesSchedule,
} from "../Models/voicesConfig";
import { VoicesWorkspace } from "../Models/voicesProfiles";

export const useVoicesConfig = (enabled: boolean) => {
  return useQuery<VoicesConfig, Error>({
    queryKey: ["voicesConfig"],
    queryFn: () => getVoicesConfig(),
    enabled,
  });
};

export const useUpdateVoicesConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<VoicesConfigPayload & VoicesCompanyVoice>) =>
      updateVoicesConfig(data),
    onSuccess: (config) => {
      queryClient.setQueryData(["voicesConfig"], config);
    },
  });
};

export const useSetVoicesBufferKey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (apiKey: string) => setVoicesBufferKey(apiKey),
    onSuccess: (config) => {
      queryClient.setQueryData(["voicesConfig"], config);
      queryClient.invalidateQueries({ queryKey: ["voicesBufferChannels"] });
    },
  });
};

export const useRemoveVoicesBufferKey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => removeVoicesBufferKey(),
    onSuccess: (config) => {
      queryClient.setQueryData(["voicesConfig"], config);
      queryClient.invalidateQueries({ queryKey: ["voicesBufferChannels"] });
    },
  });
};

export const useVoicesWorkspaces = (enabled: boolean) => {
  return useQuery<VoicesWorkspace[], Error>({
    queryKey: ["voicesWorkspaces"],
    queryFn: () => getVoicesWorkspaces(),
    enabled,
  });
};

export const useVoicesSchedules = () => {
  return useQuery<VoicesSchedule[], Error>({
    queryKey: ["voicesSchedules"],
    queryFn: () => getVoicesSchedules(),
  });
};

export const useUpdateVoicesSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { taskKey: string; data: { cron_expression?: string; enabled?: boolean } }) =>
      updateVoicesSchedule(variables.taskKey, variables.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["voicesSchedules"] });
    },
  });
};
